// src/views/Restaurant/ClientManagement/ReservationManagement/components/
// ReservationConfiguration/components/ReservationDays/ReservationDays.tsx

import React, { useState } from 'react';
import { Dropdown, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaTrash, FaPlus, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SalonReservations from './components/SalonReservations/SalonReservations';
import { PatchUpdate } from '../../../../../../../../crud/management_api/reservations_configuration';
import { ReservationConfiguration } from '../../../../types';
import './ReservationDays.scss';

/* slot → "HH:MM" */
const slotToTime = (slot: number) =>
  `${String(Math.floor(slot / 4)).padStart(2, '0')}:${String(
    (slot % 4) * 15,
  ).padStart(2, '0')}`;

const timeSlots = Array.from({ length: 96 }, (_, i) => ({
  slot: i,
  time: slotToTime(i),
}));

interface Props {
  data: ReservationConfiguration;                   // current snapshot
  onPatch: (updates: PatchUpdate[]) => Promise<void>;
}

const daysOfWeek = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];

const ReservationDays: React.FC<Props> = ({ data, onPatch }) => {
  // State to track which days are expanded (all collapsed by default)
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());

  const toggleDay = (day: number) => {
    setExpandedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      return newSet;
    });
  };

  const patchDay = async (day: number, ranges: [number, number][]) => {
    await onPatch([{ path: ['reservation_days', String(day)], value: ranges }]);
  };

  const handleChange = async (
    day: number,
    idx: number,
    key: 'start' | 'end',
    slot: number,
  ) => {
    const ranges = [...data.configuration.reservation_days[day]];
    ranges[idx] = key === 'start' ? [slot, ranges[idx][1]] : [ranges[idx][0], slot];
    await patchDay(day, ranges);
  };

  const addRange = async (day: number) => {
    const ranges = data.configuration.reservation_days[day];
    const lastEnd = ranges.length ? ranges[ranges.length - 1][1] : 0;
    await patchDay(day, [...ranges, [lastEnd + 1, lastEnd + 2]]);
  };

  const removeRange = async (day: number, idx: number) => {
    const ranges = data.configuration.reservation_days[day].filter((_, i) => i !== idx);
    await patchDay(day, ranges);
  };

  return (
    <section className="reservation-days">
      <h4 className="rd-title">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="tooltip-horarios">
              <div style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                El local gastronómico puede definir los horarios y las mesas disponibles para las reservas. 
                Puede crear más de un intervalo de horas y también definir que mesas estarán disponibles 
                en sus distintos espacios para el servicio de reservas
              </div>
            </Tooltip>
          }
        >
          <span className="info-create-item" style={{ cursor: 'pointer' }}>
            <FaInfoCircle size={15} color="#6c757d" />
          </span>
        </OverlayTrigger>
        Horarios y Disponibilidad
      </h4>
      <p className="rd-subtitle">
        Define los horarios de atención y configura qué mesas están disponibles para reservas en cada día de la semana.
      </p>
      
      {daysOfWeek.map((label, i) => {
        const day = i + 1;
        const ranges = data.configuration.reservation_days[day] || [];
        const isExpanded = expandedDays.has(day);
        return (
          <div key={day} className="rd-day">
            <div className="rd-label" onClick={() => toggleDay(day)}>
              <span className="rd-label-text">{label}</span>
              <span className="rd-label-icon">
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </div>
            {isExpanded && (
              <div className="rd-content">
              <div className="rd-ranges">
                {ranges.map(([start, end], idx) => (
                  <div key={idx} className="rd-range">
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" className="rd-toggle">
                        {slotToTime(start)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="rd-menu">
                        {timeSlots.map(({ slot, time }) => (
                          <Dropdown.Item
                            key={slot}
                            onClick={() => handleChange(day, idx, 'start', slot)}
                          >
                            {time}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <span className="rd-sep">—</span>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" className="rd-toggle">
                        {slotToTime(end)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="rd-menu">
                        {timeSlots.map(({ slot, time }) => (
                          <Dropdown.Item
                            key={slot}
                            onClick={() => handleChange(day, idx, 'end', slot)}
                          >
                            {time}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Button variant="link" className="rd-delete" onClick={() => removeRange(day, idx)}>
                      <FaTrash />
                    </Button>
                  </div>
                ))}
                <Button variant="link" className="rd-add" onClick={() => addRange(day)}>
                  <FaPlus /> Añadir
                </Button>
              </div>
              <SalonReservations day={day} data={data.configuration} onPatch={onPatch} />
            </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default ReservationDays;
